import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ProductList from "../components/Home/Products/ProductList";
import ProductModal from "../components/Home/Products/ProductModal";
import Footer from "../components/Home/Footer/Footer";
import { demoProducts } from "../data/demoProducts";
import { PRODUCTS_URL } from "../config";
import styles from "./shop.module.css";

// Normalise API and demo products into one shape for filtering.
// API products carry a populated productCollection + category; demo products
// carry flat collection/category/gender strings; brand is the last fallback.
// Case-insensitive compare: "Military Coup Deluxe" in a link must match
// "MILITARY COUP DELUXE" as named in the backoffice.
const same = (a, b) =>
  String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const normalize = (p) => ({
  ...p,
  collectionKey:
    p.productCollection?.name ||
    (typeof p.collection === "string" ? p.collection : "") ||
    p.brand ||
    "",
  typeKey: p.category?.name || (typeof p.category === "string" ? p.category : ""),
  genderKey: p.gender || "",
});

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(PRODUCTS_URL);
        const data = await response.json();
        if (response.ok && Array.isArray(data.products) && data.products.length) {
          setProducts(data.products.map(normalize));
        } else {
          setProducts(demoProducts.map(normalize));
        }
      } catch (error) {
        console.error("Failed to fetch products:", error);
        // API unreachable — show demo products instead of an empty shop
        setProducts(demoProducts.map(normalize));
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const active = {
    gender: searchParams.get("gender") || "",
    collection: searchParams.get("collection") || "",
    category: searchParams.get("category") || "",
  };

  // Filter options come from the data itself, so the shop always matches
  // whatever brands/categories exist in the store database.
  const filterGroups = useMemo(() => {
    const distinct = (key) =>
      [...new Set(products.map((p) => p[key]).filter(Boolean))].sort();
    // Unisex isn't a pill — unisex pieces show under both Men and Women
    const genders = distinct("genderKey").filter((g) => !same(g, "unisex"));
    return [
      { key: "gender", label: "Shop", options: genders },
      { key: "collection", label: "Collection", options: distinct("collectionKey") },
      { key: "category", label: "Type", options: distinct("typeKey") },
    ].filter((group) => group.options.length > 0);
  }, [products]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (same(active[key], value)) {
      next.delete(key); // toggle off
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const clearAll = () => setSearchParams({});

  const hasFilters = Object.values(active).some(Boolean);

  // Only apply a filter if its dimension exists in the data — a stale link
  // like ?gender=Men shouldn't empty the shop when products carry no gender.
  const dimensionExists = (key) =>
    filterGroups.some((group) => group.key === key);

  // Pieces with no gender (or Unisex) belong in both the Men and Women views
  const genderMatches = (p) =>
    !active.gender ||
    !dimensionExists("gender") ||
    !p.genderKey ||
    same(p.genderKey, "unisex") ||
    same(p.genderKey, active.gender);

  const visible = products.filter(
    (p) =>
      genderMatches(p) &&
      (!active.collection ||
        !dimensionExists("collection") ||
        same(p.collectionKey, active.collection)) &&
      (!active.category || !dimensionExists("category") || same(p.typeKey, active.category))
  );

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <p className={styles.eyebrow}>Shop by</p>
        <h1 className={styles.title}>
          {active.collection || active.gender
            ? [active.gender, active.collection].filter(Boolean).join(" · ")
            : "All Products"}
        </h1>
        <p className={styles.count}>
          {loading
            ? "Loading…"
            : `${visible.length} ${visible.length === 1 ? "piece" : "pieces"}`}
        </p>
      </header>

      <div className={styles.filters}>
        <div className={styles.filterInner}>
          {filterGroups.map((group) => (
            <div className={styles.filterGroup} key={group.key}>
              <span className={styles.filterLabel}>{group.label}</span>
              <div className={styles.filterOptions}>
                {group.options.map((option) => (
                  <button
                    type="button"
                    key={option}
                    className={`${styles.pill} ${
                      same(active[group.key], option) ? styles.pillActive : ""
                    }`}
                    onClick={() => setFilter(group.key, option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {hasFilters && (
            <button type="button" className={styles.clearBtn} onClick={clearAll}>
              Clear all filters
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <ProductList data={[]} setSelectedProduct={setSelectedProduct} setShowModal={setShowModal} />
      ) : visible.length > 0 ? (
        <ProductList
          data={visible}
          setSelectedProduct={setSelectedProduct}
          setShowModal={setShowModal}
        />
      ) : (
        <div className={styles.empty}>
          <p>No pieces match those filters yet.</p>
          <button type="button" className={styles.clearBtn} onClick={clearAll}>
            Clear filters
          </button>
        </div>
      )}

      {showModal && selectedProduct && (
        <ProductModal
          showModal={showModal}
          setShowModal={setShowModal}
          product={selectedProduct}
        />
      )}

      <Footer />
    </div>
  );
};

export default Shop;
